// Updated Header.jsx with fixes for horizontal overflow

import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import useTokenStore from "../../store/store";
import Openmenu from "../../images/icons/bars-solid.svg";
import Closemenu from "../../images/icons/xmark-solid.svg";
import { ThemeProps } from "../../utils/theme";
import useThemeStore from "../../store/themeStore";
import usePwaInstallStore from "../../store/pwaInstallStore";
import { PWA_SOURCE_HEADER } from "../../constants/pwaEvents";
import { isStandalone, isMobile } from "../../utils/pwaUtils";
import { isWhiteOrShade } from "../../utils/colourContrast";
import { getBuyerCreditBalance, BuyerCreditBalance } from "../../utils/properties";
import { creditIndicatorLabel } from "../../utils/creditIndicator";
import { useSavedSearchStore } from "../../store/savedSearchStore";
import { Search } from "lucide-react";
import { Helmet } from "react-helmet";

const Header = () => {
  const getToken = useTokenStore((state) => state.token);
  const user = useTokenStore((state) => state.user);
  const logout = useTokenStore((state) => state.removeToken);
  const openInstallModal = usePwaInstallStore((state) => state.openModal);
  const navigate = useNavigate();

  const logoutAction = () => {
    logout();
    navigate("/login");
  };

  // The signed-in buyer's reservation-credit balance for the header indicator.
  // The endpoint is tenant+buyer scoped and returns an empty balance for
  // un-ramped partners or on any error, so the indicator fails closed to hidden.
  const [creditBalance, setCreditBalance] = useState<BuyerCreditBalance | null>(null);
  useEffect(() => {
    // Reset on every run: the balance is buyer-specific, so a direct A → B
    // token swap must never leave A's credit badge showing while B's request
    // is in flight.
    setCreditBalance(null);
    if (!getToken) {
      return;
    }
    let active = true;
    getBuyerCreditBalance({ token: getToken })
      .then((balance) => { if (active) setCreditBalance(balance); })
      .catch(() => { /* indicator simply does not show */ });
    return () => { active = false; };
  }, [getToken]);
  const creditLabel = creditIndicatorLabel(creditBalance);

  // Unseen saved-search match count for the badge. Refreshed on every route
  // change (same cadence as page-view tracking) so a match landing mid-session
  // surfaces on the buyer's next navigation, not just the next full load.
  const location = useLocation();
  const unseenMatchCount = useSavedSearchStore((state) => state.unseenCount);
  const fetchUnseenCount = useSavedSearchStore((state) => state.fetchUnseenCount);
  useEffect(() => {
    fetchUnseenCount();
  }, [getToken, location.pathname, fetchUnseenCount]);

  useEffect(() => {
    const handleSpeakToTeam = () => {
      // This function will trigger the "Speak To Our Team" action
      const speakToTeamButton = document.querySelector('[data-hs-overlay="#hs-large-modal"]');
      if (speakToTeamButton instanceof HTMLElement) {
        speakToTeamButton.click();
      }
    };

    // Add event listener
    window.addEventListener('speak-to-team', handleSpeakToTeam);

    // Clean up
    return () => {
      window.removeEventListener('speak-to-team', handleSpeakToTeam);
    };
  }, []);

  const getThemes = useThemeStore(
    (state) => state.theme as unknown as ThemeProps
  );

  // Define inline styles for logo resizing
  const logoStyles = {
    width: window.innerWidth <= 640 ? '150px' : '240px', // Adjust width for mobile and desktop
    height: window.innerWidth <= 640 ? '60px' : '96px', // Adjust height for mobile and desktop
  };


  // Determine menu icon color based on background
  const menuIconColor = isWhiteOrShade(getThemes?.color1 || "") ? "#000000" : (getThemes?.txtColor || "#ffffff");

  // Plausible Analytics injection
  useEffect(() => {
    if (!getThemes || !getThemes.website) {
      return;
    }

    // Extract domain from website URL
    let domain: string | null = null;
    try {
      const url = new URL(getThemes.website);
      domain = url.hostname;
    } catch (error) {
      console.error('Error extracting domain for Plausible:', error);
      return;
    }

    if (!domain) {
      return;
    }

    // Check if Plausible script is already loaded
    const existingScript = document.querySelector(`script[data-domain="${domain}"]`);
    if (existingScript) {
      return;
    }

    // Inject Plausible tracking script
    const script = document.createElement('script');
    script.defer = true;
    script.setAttribute('data-domain', domain);
    script.src = 'https://analytics.whitelabelproperty.com/js/script.js';
    
    script.onerror = () => {
      console.error('Failed to load Plausible script');
    };
    
    document.head.appendChild(script);

    // Cleanup function - don't remove script on cleanup
    return () => {
      // Script should persist across re-renders
    };
  }, [getThemes]);

  return (
    <>
    <header
      className="fixed flex flex-wrap sm:justify-start sm:flex-nowrap z-[1000] w-full bg-white text-sm py-4 overflow-hidden"
      style={{ background: getThemes && getThemes!.color1 }}
    >
      <Helmet>
        <title>{getThemes && getThemes.name}</title>
        <link rel="shortcut icon" href={getThemes && getThemes.favIcon} />
        
        {/* GA4 Consent Mode Initialization - Always load */}
        <script>
          {`window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          
          // Set default consent state - deny all non-essential
          gtag('consent', 'default', {
            ad_storage: 'denied',
            analytics_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
            functionality_storage: 'granted',
            security_storage: 'granted'
          });`}
        </script>
        
        {/* GA4 Scripts - Blocked until analytics consent */}
        {getThemes && getThemes.googleAnalytics ? (
          <script
            type="text/plain"
            data-category="analytics"
            data-src={`https://www.googletagmanager.com/gtag/js?id=${getThemes.googleAnalytics}`}
          />
        ) : null}
        {getThemes && getThemes.googleAnalytics ? (
          <script type="text/plain" data-category="analytics">
            {`window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
      
        gtag('config', '${getThemes.googleAnalytics}');`}
          </script>
        ) : null}
      
      </Helmet>
      <nav
        className="max-w-7xl w-full mx-auto lg:px-0 px-5 sm:flex sm:items-center sm:justify-between"
        aria-label="Global"
      >
        <div className="flex items-center justify-between">
          <Link
            className="flex-none text-xl font-semibold"
            to={getThemes && getThemes!.subwebsite}
          >
             <img
              src={getThemes && getThemes!.logo}
              style={logoStyles}
              className="object-contain object-left"
              alt="Logo"
            />
          </Link>
          <div className="sm:hidden noprint">
            <button
              type="button"
              className="hs-collapse-toggle p-2 inline-flex justify-center items-center gap-2 font-medium shadow-sm align-middle transition-all text-sm"
              data-hs-collapse="#navbar-with-mega-menu"
              aria-controls="navbar-with-mega-menu"
              aria-label="Toggle navigation"
              style={{ color: menuIconColor }}
            >
              <img
                src={Openmenu}
                className="hs-collapse-open:hidden w-6 h-6"
                alt=""
                style={{
                  filter: isWhiteOrShade(getThemes?.color1 || "") ? "brightness(0)" : "brightness(0) invert(1)",
                }}
              />
              <img
                src={Closemenu}
                className="hs-collapse-open:block hidden w-6 h-6"
                alt=""
                style={{
                  filter: isWhiteOrShade(getThemes?.color1 || "") ? "brightness(0)" : "brightness(0) invert(1)",
                }}
              />
            </button>
          </div>
        </div>
        <div
          id="navbar-with-mega-menu"
          className="hs-collapse hidden overflow-hidden transition-all duration-300 basis-full grow sm:block noprint"
        >
          <div className="flex flex-col gap-5 mt-5 sm:flex-row sm:items-center sm:justify-end sm:mt-0 sm:pl-5">
            <a
              className="font-medium text-left text-white-f2f9fa"
              href={getThemes && getThemes.subwebsite}
              style={{ color: getThemes && getThemes!.txtColor }}
            >
              Home
            </a>
            {getThemes && getThemes.website === "https://trustbricks.co.uk" && (
              <div className="hs-dropdown [--strategy:static] sm:[--strategy:fixed] lg:[--trigger:hover] sm:[--trigger:click] [--adaptive:none]">
                <button
                  id="hs-mega-menu-basic-dr"
                  type="button"
                  className="flex items-center w-full text-white-f2f9fa font-medium"
                  style={{ color: getThemes && getThemes!.txtColor }}
                >
                  Who's it for
                  <svg
                    className="ml-2 w-2.5 h-2.5"
                    style={{ color: menuIconColor }}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 5L8.16086 10.6869C8.35239 10.8637 8.64761 10.8637 8.83914 10.6869L15 5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    ></path>
                  </svg>
                </button>

                <div
                  className="hs-dropdown-menu transition-[opacity,margin] duration-[0.1ms] sm:duration-[150ms] hs-dropdown-open:opacity-100 lg:border opacity-0 sm:w-48 z-10 bg-primarycolor sm:shadow-md rounded-lg p-2 before:absolute top-full before:-top-5 before:left-0 before:w-full before:h-5 hidden"
                  style={{ background: getThemes && getThemes.color1 }}
                >
                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm text-white-f2f9fa hover:bg-white-f2f9fa hover:text-primarycolor"
                    to="https://trustbricks.co.uk/first-time-property-investors/"
                    target={"_blank"}
                    style={{ color: getThemes && getThemes!.txtColor }}
                  >
                    New Investors
                  </Link>

                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm text-white-f2f9fa hover:bg-white-f2f9fa hover:text-primarycolor"
                    to="https://trustbricks.co.uk/invest-in-property/"
                    target={"_blank"}
                    style={{ color: getThemes && getThemes!.txtColor }}
                  >
                    Portfolio Investors
                  </Link>
                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm text-white-f2f9fa hover:bg-white-f2f9fa hover:text-primarycolor"
                    to="https://trustbricks.co.uk/refer-an-investor/"
                    target={"_blank"}
                    style={{ color: getThemes && getThemes!.txtColor }}
                  >
                    Refer an Investor
                  </Link>
                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm text-white-f2f9fa hover:bg-white-f2f9fa hover:text-primarycolor"
                    to="https://trustbricks.co.uk/white-label/"
                    target={"_blank"}
                    style={{ color: getThemes && getThemes!.txtColor }}
                  >
                    White Label Solution
                  </Link>
                </div>
              </div>
            )}

            {getThemes && getThemes.website === "https://trustbricks.co.uk" && (
              <Link
                className="font-medium text-left text-white-f2f9fa"
                to="https://trustbricks.co.uk/#howitworks"
                target={"_blank"}
                aria-current="page"
                style={{ color: getThemes && getThemes!.txtColor }}
              >
                How It Works
              </Link>
            )}

            <div className="hs-dropdown [--strategy:static] sm:[--strategy:fixed] lg:[--trigger:hover] sm:[--trigger:click] [--adaptive:none]">
              <button
                id="hs-mega-menu-properties-dr"
                type="button"
                className="flex items-center w-full text-white-f2f9fa font-medium"
                style={{ color: getThemes && getThemes!.txtColor }}
              >
                Properties
                <svg
                  className="ml-2 w-2.5 h-2.5"
                  style={{ color: menuIconColor }}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 5L8.16086 10.6869C8.35239 10.8637 8.64761 10.8637 8.83914 10.6869L15 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  ></path>
                </svg>
              </button>

              <div
                className="hs-dropdown-menu transition-[opacity,margin] duration-[0.1ms] sm:duration-[150ms] hs-dropdown-open:opacity-100 lg:border opacity-0 sm:w-48 z-10 bg-primarycolor sm:shadow-md rounded-lg p-2 before:absolute top-full before:-top-5 before:left-0 before:w-full before:h-5 hidden"
                style={{ background: getThemes && getThemes.color1 }}
              >
                <Link
                  className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm text-white-f2f9fa hover:bg-white-f2f9fa"
                  to="/listing"
                  data-hs-collapse="#navbar-with-mega-menu"
                  style={{ color: getThemes && getThemes!.txtColor }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "#000000"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = getThemes?.txtColor ?? "#ffffff"; }}
                >
                  Off Market
                </Link>
                <Link
                  className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm text-white-f2f9fa hover:bg-white-f2f9fa"
                  to="/new-builds"
                  data-hs-collapse="#navbar-with-mega-menu"
                  style={{ color: getThemes && getThemes!.txtColor }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "#000000"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = getThemes?.txtColor ?? "#ffffff"; }}
                >
                  New Build
                </Link>
              </div>
            </div>

            <Link
              className="font-medium text-left text-white-f2f9fa"
              to="/Services"
              data-hs-collapse="#navbar-with-mega-menu"
              style={{ color: getThemes && getThemes!.txtColor }}
            >
              Services
            </Link>

            {getThemes && getThemes.website === "https://trustbricks.co.uk" && (
              <div className="hs-dropdown [--strategy:static] sm:[--strategy:fixed] lg:[--trigger:hover] sm:[--trigger:click] [--adaptive:none] ">
                <button
                  id="hs-mega-menu-basic-dr"
                  type="button"
                  className="flex items-center w-full text-white-f2f9fa font-medium"
                  style={{ color: getThemes && getThemes!.txtColor }}
                >
                  Resources
                  <svg
                    className="ml-2 w-2.5 h-2.5"
                    style={{ color: menuIconColor }}
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 5L8.16086 10.6869C8.35239 10.8637 8.64761 10.8637 8.83914 10.6869L15 5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    ></path>
                  </svg>
                </button>

                <div
                  className="hs-dropdown-menu transition-[opacity,margin] duration-[0.1ms] sm:duration-[150ms] hs-dropdown-open:opacity-100 opacity-0 sm:w-48 z-10 bg-primarycolor lg:border  sm:shadow-md rounded-lg p-2 before:absolute top-full before:-top-5 before:left-0 before:w-full before:h-5 hidden"
                  style={{ background: getThemes && getThemes.color1 }}
                >
                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm text-white-f2f9fa hover:bg-white-f2f9fa hover:text-primarycolor"
                    to="https://www.youtube.com/channel/UCtbHPeoFa4tQaWSJLrOsrcQ"
                    target={"_blank"}
                    style={{ color: getThemes && getThemes!.txtColor }}
                  >
                    Videos
                  </Link>
                  {}
                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm text-white-f2f9fa hover:bg-white-f2f9fa hover:text-primarycolor"
                    to="https://trustbricks.co.uk/blog/"
                    target={"_blank"}
                    style={{ color: getThemes && getThemes!.txtColor }}
                  >
                    Blog
                  </Link>
                </div>
              </div>
            )}
            {getToken === undefined && (
              <Link
                className="flex items-center gap-x-2 sm:py-2 font-medium text-left text-white-f2f9fa"
                to="/login"
                data-hs-collapse="#navbar-with-mega-menu"
                style={{ color: getThemes && getThemes!.txtColor }}
              >
                <svg
                  className="w-4 h-4"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                </svg>
                Login
              </Link>
            )}
            {getToken === undefined && (
              <Link
                className="flex items-center gap-x-2 sm:py-2 font-medium text-left text-white-f2f9fa"
                to="/register"
                data-hs-collapse="#navbar-with-mega-menu"
                style={{ color: getThemes && getThemes!.txtColor }}
              >
                <svg
                  className="w-4 h-4"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z" />
                </svg>
                Sign Up
              </Link>
            )}
            {/* User Section - Only show when signed in */}
            {getToken !== undefined && (
              <div className="hs-dropdown [--strategy:static] sm:[--strategy:fixed] lg:[--trigger:hover] sm:[--trigger:click] [--adaptive:none]">
              <button
                  type="button"
                  className="flex items-center gap-x-2 text-white-f2f9fa font-medium"
                style={{ color: getThemes && getThemes!.txtColor }}
              >
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                      {user?.profile_image ? (
                        <img
                          src={user.profile_image}
                          alt={user.fullname || 'User'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-gray-600 font-semibold text-sm">
                          {user?.fullname?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                    {/* Notification dot (reservation credit and/or unseen
                        saved-search matches) — bordered in the header colour
                        so it pops on light and dark themes alike. */}
                    {(creditLabel || unseenMatchCount > 0) && (
                      <span
                        className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-amber-400 border-2"
                        style={{ borderColor: getThemes?.color1 || "#ffffff" }}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <span className="hidden sm:block">{user?.fullname || 'User'}</span>
                  <svg
                    className="ml-1 w-2.5 h-2.5"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2 5L8.16086 10.6869C8.35239 10.8637 8.64761 10.8637 8.83914 10.6869L15 5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>

                <div
                  className="hs-dropdown-menu transition-[opacity,margin] duration-[0.1ms] sm:duration-[150ms] hs-dropdown-open:opacity-100 opacity-0 sm:w-64 z-10 bg-primarycolor lg:border sm:shadow-md rounded-lg p-2 before:absolute top-full before:-top-5 before:left-0 before:w-full before:h-5 hidden"
                  style={{ background: getThemes && getThemes.color1 }}
                >
                  {/* User Info */}
                  <div className="px-3 py-2 border-b border-gray-300 mb-2">
                    <p className="text-sm font-semibold text-white-f2f9fa" style={{ color: getThemes && getThemes!.txtColor }}>
                      {user?.fullname || 'User'}
                    </p>
                    <p className="text-xs text-gray-300" style={{ color: getThemes && getThemes!.txtColor }}>
                      {user?.email || 'Loading...'}
                    </p>
                    <p className="text-xs text-gray-300" style={{ color: getThemes && getThemes!.txtColor }}>
                      {user?.userHandle || ''}
                    </p>
                    {creditLabel && (
                      <p
                        className="mt-1.5 flex items-center gap-x-1.5 text-xs font-semibold"
                        style={{ color: getThemes && getThemes!.txtColor }}
                      >
                        <span className="w-2 h-2 rounded-full bg-amber-400" aria-hidden="true" />
                        {creditLabel}
                      </p>
                    )}
                  </div>

                  {/* User Menu Items */}
                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm hover:bg-white-f2f9fa transition-colors"
                    to="/profile"
                    data-hs-collapse="#navbar-with-mega-menu"
                    style={{ 
                      color: getThemes && getThemes!.txtColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = (getThemes && getThemes!.txtColor) || '#ffffff';
                    }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                    </svg>
                    My Profile
                  </Link>

                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm hover:bg-white-f2f9fa transition-colors"
                    to="/dashboard"
                    data-hs-collapse="#navbar-with-mega-menu"
                    style={{ 
                      color: getThemes && getThemes!.txtColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = (getThemes && getThemes!.txtColor) || '#ffffff';
                    }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                    </svg>
                    Dashboard
                  </Link>

                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm hover:bg-white-f2f9fa transition-colors"
                    to="/saved-searches"
                    data-hs-collapse="#navbar-with-mega-menu"
                    style={{
                      color: getThemes && getThemes!.txtColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = (getThemes && getThemes!.txtColor) || '#ffffff';
                    }}
                  >
                    <Search className="w-4 h-4" />
                    Saved Searches
                    {unseenMatchCount > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-xs font-semibold bg-amber-400 text-black">
                        {unseenMatchCount}
                      </span>
                    )}
                  </Link>

                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm hover:bg-white-f2f9fa transition-colors"
                    to="/settings"
                    data-hs-collapse="#navbar-with-mega-menu"
                    style={{ 
                      color: getThemes && getThemes!.txtColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = (getThemes && getThemes!.txtColor) || '#ffffff';
                    }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                    Settings
                  </Link>

                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm hover:bg-white-f2f9fa transition-colors"
                    to="/notifications"
                    data-hs-collapse="#navbar-with-mega-menu"
                    style={{ 
                      color: getThemes && getThemes!.txtColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = (getThemes && getThemes!.txtColor) || '#ffffff';
                    }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" clipRule="evenodd" />
                    </svg>
                    Updates
                  </Link>

                  <Link
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm hover:bg-white-f2f9fa transition-colors"
                    to="/feedback"
                    data-hs-collapse="#navbar-with-mega-menu"
                    style={{ 
                      color: getThemes && getThemes!.txtColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = (getThemes && getThemes!.txtColor) || '#ffffff';
                    }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                    </svg>
                    Feedback
                  </Link>

                  <div className="border-t border-gray-300 my-2"></div>

                  <button
                    onClick={logoutAction}
                    className="flex items-center gap-x-3.5 py-2 px-3 rounded-md text-sm hover:bg-white-f2f9fa transition-colors w-full text-left"
                    style={{ 
                      color: getThemes && getThemes!.txtColor,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#000000';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = (getThemes && getThemes!.txtColor) || '#ffffff';
                    }}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                    </svg>
                    Logout
              </button>
                </div>
              </div>
            )}

            {getThemes?.pwaEnabled && isMobile() && !isStandalone() && (
              <button
                type="button"
                className="flex items-center gap-x-2 font-medium text-left sm:mr-2"
                data-hs-collapse="#navbar-with-mega-menu"
                style={{ color: getThemes && getThemes!.txtColor }}
                onClick={() => openInstallModal(PWA_SOURCE_HEADER)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#000000";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = (getThemes && getThemes!.txtColor) || "#ffffff";
                }}
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v7.586l2.293-2.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 11.586V4a1 1 0 011-1z" clipRule="evenodd" />
                  <path d="M3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                </svg>
                Install App
              </button>
            )}

            <div
              className="py-2.5 text-center rounded-md font-poppins cursor-pointer mx-auto sm:mx-0 max-w-full w-44"
              data-hs-overlay="#hs-large-modal"
              style={{
                background: getThemes?.color2 ?? "#0055cc",
                color: getThemes?.buttonTxtColour ?? (isWhiteOrShade(getThemes?.color2 || "") ? "#000000" : getThemes?.txtColor ?? "#ffffff"),
                border: `1px solid ${getThemes?.txtColor ?? "#ffffff"}`,
                transition: "all 0.3s ease",
              }}
              onMouseOver={e => {
                const hoverBg = getThemes?.color1 ?? "#222222";
                e.currentTarget.style.background = hoverBg;
                e.currentTarget.style.color = getThemes?.buttonHoverTxtColour ?? (isWhiteOrShade(hoverBg) ? "#000000" : (getThemes?.txtColor ?? "#ffffff"));
              }}
              onMouseOut={e => {
                const normalBg = getThemes?.color2 ?? "#0055cc";
                e.currentTarget.style.background = normalBg;
                e.currentTarget.style.color = getThemes?.buttonTxtColour ?? (isWhiteOrShade(normalBg) ? "#000000" : getThemes?.txtColor ?? "#ffffff");
              }}
            >
              Speak To Our Team
            </div>
          </div>
        </div>
      </nav>
    </header>
    </>
  );
};

export default Header;